/**
 * Bulk Regularize · admin surface.
 *
 * Route: /admin/attendance/bulk-regularize
 * Auth : `employee:admin` capability (super_admin holds it).
 *
 * Purpose: back-fill a date range of check-in/check-out events for an
 * employee who could not self-serve regularization (no computer
 * access, extended field trip, etc).  Weekday filter, hard skip
 * policy, and a preview-first workflow keep the action safe.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ArrowClockwise, CalendarBlank, CheckCircle, Warning, MagicWand, User,
} from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import SearchableSelect from "../../components/SearchableSelect";


// ISO weekday · 0 = Monday .. 6 = Sunday.  Matches Python's
// datetime.date.weekday().
const WEEKDAYS = [
    { code: 0, label: "Mon" },
    { code: 1, label: "Tue" },
    { code: 2, label: "Wed" },
    { code: 3, label: "Thu" },
    { code: 4, label: "Fri" },
    { code: 5, label: "Sat" },
    { code: 6, label: "Sun" },
];

const REASON_LABEL = {
    weekday_not_in_filter: "Weekday not selected",
    excluded_by_operator: "Excluded by you",
    future_date: "Future date",
    existing_events_present: "Already has check-in/out events",
};

function reasonLabel(reason) {
    if (REASON_LABEL[reason]) return REASON_LABEL[reason];
    if (reason.startsWith("snapshot_status_already_")) {
        return `Already ${reason.replace("snapshot_status_already_", "")}`;
    }
    if (reason.startsWith("persist_error:")) {
        return `Insert failed (${reason.split(":")[1]})`;
    }
    return reason;
}


function todayIso() {
    const d = new Date();
    return d.toISOString().slice(0, 10);
}

function isoDaysAgo(days) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
}


export default function AttendanceBulkRegularize() {
    const { can } = useAuth();
    const isAdmin = can?.("employee:admin");

    const [employees, setEmployees] = useState([]);
    const [employeeId, setEmployeeId] = useState("");
    const [fromDate, setFromDate] = useState(isoDaysAgo(30));
    const [toDate, setToDate] = useState(todayIso());
    const [checkIn, setCheckIn] = useState("09:30");
    const [checkOut, setCheckOut] = useState("18:30");
    const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]);
    const [remarks, setRemarks] = useState("");
    const [excludeDatesRaw, setExcludeDatesRaw] = useState("");

    const [previewLoading, setPreviewLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [preview, setPreview] = useState(null);
    const [applyResult, setApplyResult] = useState(null);

    // Load the employees dropdown once.
    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const r = await api.get("/hr/employee/list", { params: { limit: 500 } });
                const items = r.data.items || r.data || [];
                items.sort((a, b) =>
                    (a.full_name || a.name || a.official_email || "").localeCompare(
                        b.full_name || b.name || b.official_email || "",
                    ),
                );
                setEmployees(items);
            } catch (err) {
                toast.error(formatApiError(err) || "Failed to load employees");
            }
        })();
    }, [isAdmin]);

    const toggleWeekday = (code) => {
        setWeekdays((prev) =>
            prev.includes(code) ? prev.filter((d) => d !== code) : [...prev, code].sort(),
        );
    };

    const parsedExcludeDates = useMemo(() => {
        // Comma / whitespace / newline separated · YYYY-MM-DD only.
        const parts = (excludeDatesRaw || "")
            .split(/[,\s]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        const good = [];
        const bad = [];
        for (const p of parts) {
            if (/^\d{4}-\d{2}-\d{2}$/.test(p) && !isNaN(Date.parse(p))) {
                good.push(p);
            } else {
                bad.push(p);
            }
        }
        return { good: Array.from(new Set(good)).sort(), bad };
    }, [excludeDatesRaw]);

    const buildBody = useCallback(
        (dryRun) => ({
            subject_employee_id: employeeId,
            from_date: fromDate,
            to_date: toDate,
            weekdays,
            check_in_time: checkIn,
            check_out_time: checkOut,
            remarks: remarks.trim(),
            exclude_dates: parsedExcludeDates.good,
            dry_run: dryRun,
        }),
        [employeeId, fromDate, toDate, weekdays, checkIn, checkOut, remarks, parsedExcludeDates.good],
    );

    const canPreview = useMemo(() => {
        return (
            employeeId &&
            fromDate &&
            toDate &&
            weekdays.length > 0 &&
            /^\d{2}:\d{2}$/.test(checkIn) &&
            /^\d{2}:\d{2}$/.test(checkOut) &&
            remarks.trim().length >= 10 &&
            parsedExcludeDates.bad.length === 0 &&
            !previewLoading &&
            !applying
        );
    }, [employeeId, fromDate, toDate, weekdays, checkIn, checkOut, remarks, parsedExcludeDates.bad.length, previewLoading, applying]);

    const canApply = !!preview && preview.applied_count > 0 && !applying;

    const runPreview = async () => {
        setPreviewLoading(true);
        setApplyResult(null);
        try {
            const r = await api.post("/attendance/event/bulk-regularize", buildBody(true));
            setPreview(r.data);
            if (r.data.applied_count === 0) {
                toast.warning("Preview: nothing to apply — every date was skipped.");
            } else {
                toast.success(`Preview ready — ${r.data.applied_count} of ${r.data.scanned} days will be regularized.`);
            }
        } catch (err) {
            toast.error(formatApiError(err) || "Preview failed");
            setPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const runApply = async () => {
        if (!window.confirm(
            `This will insert ${preview.applied_count} check-in + ${preview.applied_count} check-out events on ${preview.subject_employee_id}'s timeline (dates ${preview.from_date} → ${preview.to_date}).  Continue?`,
        )) return;
        setApplying(true);
        try {
            const r = await api.post("/attendance/event/bulk-regularize", buildBody(false));
            setApplyResult(r.data);
            setPreview(null);
            toast.success(`Applied · run ${r.data.run_id} · ${r.data.applied_count} dates regularized.`);
        } catch (err) {
            toast.error(formatApiError(err) || "Apply failed");
        } finally {
            setApplying(false);
        }
    };

    if (!isAdmin) {
        return (
            <EmptyState
                title="Not authorised"
                description="The Bulk Regularize surface is restricted to super_admin operators."
                data-testid="bulk-rgz-forbidden"
            />
        );
    }

    return (
        <div className="space-y-6" data-testid="bulk-rgz-page">
            <PageHeader
                eyebrow="ATTENDANCE · ADMIN"
                title="Bulk Regularize"
                description="Back-fill a paired check-in + check-out for every date in a range · Preview first · Days that already have events or a decisive status are skipped automatically."
            />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Left · configuration */}
                <section className="lg:col-span-2 space-y-5 rounded-lg border bg-white p-5">
                    <div>
                        <Label htmlFor="employee">
                            <User className="inline mr-1 h-4 w-4" />
                            Employee
                        </Label>
                        <SearchableSelect
                            value={employeeId}
                            onChange={setEmployeeId}
                            options={employees.map((e) => ({
                                value: e.id,
                                label:
                                    e.full_name ||
                                    e.name ||
                                    e.official_email ||
                                    e.personal_email ||
                                    e.email ||
                                    e.employee_code ||
                                    e.id,
                                hint: [
                                    e.employee_code,
                                    e.department,
                                    e.designation,
                                ]
                                    .filter(Boolean)
                                    .join(" · "),
                            }))}
                            placeholder={
                                employees.length
                                    ? "Search by name, code, or department…"
                                    : "Loading employees…"
                            }
                            searchPlaceholder="Type name or code (e.g. Anurag, EMP-0024)"
                            emptyText="No matching employee. Try a shorter query."
                            testId="bulk-rgz-employee"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="from">
                                <CalendarBlank className="inline mr-1 h-4 w-4" />
                                From
                            </Label>
                            <Input
                                id="from"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                max={todayIso()}
                                data-testid="bulk-rgz-from"
                            />
                        </div>
                        <div>
                            <Label htmlFor="to">
                                <CalendarBlank className="inline mr-1 h-4 w-4" />
                                To
                            </Label>
                            <Input
                                id="to"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                max={todayIso()}
                                data-testid="bulk-rgz-to"
                            />
                        </div>
                    </div>

                    <div>
                        <Label>Weekdays (only these dates in the range get regularized)</Label>
                        <div className="mt-1 flex flex-wrap gap-2">
                            {WEEKDAYS.map((wd) => {
                                const on = weekdays.includes(wd.code);
                                return (
                                    <button
                                        key={wd.code}
                                        type="button"
                                        onClick={() => toggleWeekday(wd.code)}
                                        data-testid={`bulk-rgz-wd-${wd.code}`}
                                        className={`rounded-full px-3 py-1 text-xs font-medium border transition ${on
                                            ? "bg-slate-900 text-white border-slate-900"
                                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                                            }`}
                                    >
                                        {wd.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Sat / Sun toggle applies only if the weekly-off in the employee&apos;s shift does NOT already mark them.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="ci">Check-In time (IST)</Label>
                            <Input
                                id="ci"
                                type="time"
                                value={checkIn}
                                onChange={(e) => setCheckIn(e.target.value)}
                                data-testid="bulk-rgz-ci"
                            />
                        </div>
                        <div>
                            <Label htmlFor="co">Check-Out time (IST)</Label>
                            <Input
                                id="co"
                                type="time"
                                value={checkOut}
                                onChange={(e) => setCheckOut(e.target.value)}
                                data-testid="bulk-rgz-co"
                            />
                            {checkOut <= checkIn && (
                                <p className="mt-1 text-[11px] text-amber-700">
                                    Check-out is on or before check-in — will be treated as an overnight shift (next calendar day).
                                </p>
                            )}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="exclude">Exclude specific dates (optional · e.g. leave days)</Label>
                        <Input
                            id="exclude"
                            value={excludeDatesRaw}
                            onChange={(e) => setExcludeDatesRaw(e.target.value)}
                            placeholder="2026-07-08, 2026-07-09"
                            data-testid="bulk-rgz-exclude"
                        />
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                            {parsedExcludeDates.good.length > 0 && (
                                <span className="text-slate-600">
                                    Will skip: {parsedExcludeDates.good.join(", ")}
                                </span>
                            )}
                            {parsedExcludeDates.bad.length > 0 && (
                                <span className="text-rose-700">
                                    Invalid: {parsedExcludeDates.bad.join(", ")} — use YYYY-MM-DD
                                </span>
                            )}
                            {parsedExcludeDates.good.length === 0 && parsedExcludeDates.bad.length === 0 && (
                                <span className="text-muted-foreground">
                                    Comma or space separated · YYYY-MM-DD
                                </span>
                            )}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="remarks">Reason / Justification (mandatory · min 10 chars)</Label>
                        <Textarea
                            id="remarks"
                            rows={3}
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder='E.g. "Anurag was on-site 09:30-18:30 every weekday of Jul 2026 · verified in-person · has no computer access to self-serve regularization."'
                            maxLength={1000}
                            data-testid="bulk-rgz-remarks"
                        />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            {remarks.trim().length}/1000
                        </p>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            onClick={runPreview}
                            disabled={!canPreview}
                            data-testid="bulk-rgz-preview-btn"
                        >
                            {previewLoading ? (
                                <>
                                    <ArrowClockwise className="mr-1.5 h-4 w-4 animate-spin" />
                                    Previewing…
                                </>
                            ) : (
                                <>
                                    <ArrowClockwise className="mr-1.5 h-4 w-4" />
                                    Preview
                                </>
                            )}
                        </Button>
                        <Button
                            variant="default"
                            onClick={runApply}
                            disabled={!canApply}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="bulk-rgz-apply-btn"
                        >
                            <MagicWand className="mr-1.5 h-4 w-4" />
                            {applying ? "Applying…" : `Apply · ${preview?.applied_count ?? 0} dates`}
                        </Button>
                    </div>
                </section>

                {/* Right · result panel */}
                <aside className="space-y-4">
                    {(preview || applyResult) && (
                        <ResultPanel data={applyResult || preview} isApplied={!!applyResult} />
                    )}
                    {!preview && !applyResult && (
                        <div className="rounded-lg border border-dashed bg-slate-50 p-6 text-center text-sm text-slate-500">
                            Configure the form, click <b>Preview</b> to see which dates will be regularized, then <b>Apply</b> to write.
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}


function ResultPanel({ data, isApplied }) {
    return (
        <div
            className="rounded-lg border bg-white p-4"
            data-testid={isApplied ? "bulk-rgz-result-applied" : "bulk-rgz-result-preview"}
        >
            <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">
                    {isApplied ? (
                        <span className="text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle weight="fill" className="h-4 w-4" />
                            Applied — {data.run_id}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-slate-700">
                            <MagicWand className="h-4 w-4" />
                            Preview — {data.run_id}
                        </span>
                    )}
                </div>
                <Badge variant="outline" className="text-[10px]">
                    dry_run · {String(data.dry_run)}
                </Badge>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Stat label="Scanned" value={data.scanned} />
                <Stat label="Applied" value={data.applied_count} tone="emerald" />
                <Stat label="Skipped" value={data.skipped_count} tone="rose" />
            </div>

            {data.applied?.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                        {isApplied ? "Regularized" : "Will regularize"}
                    </div>
                    <ul className="mt-1 space-y-0.5 text-xs">
                        {data.applied.map((a) => (
                            <li key={a.date} className="flex items-center gap-2 font-mono">
                                <CheckCircle className="h-3 w-3 text-emerald-600" />
                                {a.date}
                                {isApplied && a.check_in_event_id && (
                                    <span className="text-[10px] text-muted-foreground truncate">
                                        {a.check_in_event_id} · {a.check_out_event_id}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {data.skipped?.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">
                        Skipped
                    </div>
                    <ul className="mt-1 space-y-0.5 text-xs">
                        {data.skipped.map((s) => (
                            <li key={s.date} className="flex items-center gap-2 font-mono">
                                <Warning className="h-3 w-3 text-rose-500" />
                                <span>{s.date}</span>
                                <span className="text-[10px] text-muted-foreground">
                                    · {reasonLabel(s.reason)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}


function Stat({ label, value, tone }) {
    const toneClass =
        tone === "emerald"
            ? "text-emerald-700 bg-emerald-50 border-emerald-100"
            : tone === "rose"
                ? "text-rose-700 bg-rose-50 border-rose-100"
                : "text-slate-700 bg-slate-50 border-slate-100";
    return (
        <div className={`rounded-md border p-2 ${toneClass}`}>
            <div className="text-xs uppercase tracking-wider">{label}</div>
            <div className="text-lg font-bold">{value}</div>
        </div>
    );
}
